import React, { useEffect, useRef, useState } from 'react';
import { Button, DotLoading, Input, Modal, Toast } from 'antd-mobile';
import { satisfactionApi } from '../api';

const HR_NAME = 'Марина';
const HR_ROLE = 'виртуальный помощник отдела кадров';

function Bubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
      <div
        style={{
          maxWidth: '82%',
          padding: '10px 14px',
          borderRadius: 16,
          borderBottomLeftRadius: isUser ? 16 : 4,
          borderBottomRightRadius: isUser ? 4 : 16,
          background: isUser ? '#1890ff' : '#fff',
          color: isUser ? '#fff' : '#262626',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          fontSize: 15,
          lineHeight: 1.45,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {msg.content}
      </div>
    </div>
  );
}

function SatisfactionPage() {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const listRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  };

  useEffect(() => {
    satisfactionApi
      .session()
      .then((res) => {
        setSession(res.data.session);
        setMessages(res.data.messages || []);
        scrollToBottom();
      })
      .catch((err) => {
        Toast.show({ icon: 'fail', content: err?.response?.data?.detail || 'Не удалось открыть беседу' });
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending || !session || session.status !== 'active') return;
    setInput('');
    setSending(true);
    const optimistic = { id: `local-${Date.now()}`, role: 'user', content: text };
    setMessages((prev) => [...prev, optimistic]);
    scrollToBottom();
    try {
      const res = await satisfactionApi.send(session.id, text);
      setMessages((prev) => [...prev, res.data.message]);
      scrollToBottom();
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setInput(text);
      Toast.show({ icon: 'fail', content: err?.response?.data?.detail || 'Ошибка отправки' });
    } finally {
      setSending(false);
    }
  };

  const handleFinish = async () => {
    const ok = await Modal.confirm({
      content: 'Завершить беседу? Отдел кадров получит сводку, а при следующем входе начнётся новая беседа.',
      confirmText: 'Завершить',
      cancelText: 'Продолжить',
    });
    if (!ok) return;
    setFinishing(true);
    try {
      const res = await satisfactionApi.finish(session.id);
      setSession(res.data.session);
      Toast.show({ icon: 'success', content: 'Спасибо! Беседа записана' });
    } catch (err) {
      Toast.show({ icon: 'fail', content: err?.response?.data?.detail || 'Не удалось завершить беседу' });
    } finally {
      setFinishing(false);
    }
  };

  const finished = session?.status === 'finished';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 45px - 50px - 24px)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 14px',
          borderRadius: 14,
          background: '#fff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            flexShrink: 0,
          }}
        >
          👩‍💼
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{HR_NAME}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{HR_ROLE}</div>
        </div>
        {!finished && session && (
          <Button size="small" color="primary" fill="outline" loading={finishing} onClick={handleFinish}>
            Завершить
          </Button>
        )}
      </div>

      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '4px 2px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
            <DotLoading /> Загрузка беседы...
          </div>
        )}
        {!loading && messages.map((m) => <Bubble key={m.id} msg={m} />)}
        {sending && (
          <div style={{ color: '#999', fontSize: 13, padding: '0 6px' }}>
            {HR_NAME} печатает <DotLoading />
          </div>
        )}
        {finished && (
          <div
            style={{
              textAlign: 'center',
              color: '#52c41a',
              fontSize: 14,
              padding: '12px 16px',
              background: '#f6ffed',
              borderRadius: 12,
              margin: '8px 0',
            }}
          >
            Беседа завершена. Спасибо за откровенность — сводка передана в отдел кадров.
          </div>
        )}
      </div>

      {!finished && (
        <div style={{ display: 'flex', gap: 8, paddingTop: 8 }}>
          <Input
            placeholder="Написать сообщение..."
            value={input}
            onChange={setInput}
            onEnterPress={handleSend}
            enterKeyHint="send"
            disabled={sending}
            style={{
              flex: 1,
              background: '#fff',
              borderRadius: 20,
              padding: '8px 14px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            }}
          />
          <Button color="primary" onClick={handleSend} disabled={!input.trim() || sending} style={{ borderRadius: 20 }}>
            ➤
          </Button>
        </div>
      )}
    </div>
  );
}

export default SatisfactionPage;
